import { useEffect } from 'react';

import { AlertTriangleIcon } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { WaitingSoundName } from '@/hooks/useWaitingSounds';

import { TTSCardProps } from './types';
import { useTTSVoiceOptions } from './useTTSVoiceOptions';
import WaitingSoundsSelector from './WaitingSoundsSelector';

/**
 * MiniMax Voice configuration component
 *
 * Provides form fields for:
 * - Voice selection (combobox)
 * - Speed slider (0.5-2)
 * - Volume slider (0-10)
 * - Pitch slider (-12 to 12)
 * - English normalization (switch)
 *
 * MiniMax voices are multilingual; the language is selected via language_boost
 * (handled by the shared language selector), so voices are not filtered by language.
 */
const MiniMaxVoice = ({ basePath = 'config.tts_minimax', strategy }: TTSCardProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();

  const selectedLanguage = useWatch({ control, name: `${basePath}.language_boost` });
  const englishNormalization = useWatch({ control, name: `${basePath}.english_normalization` });
  const thinkingSoundName = useWatch({ control, name: 'config.thinking_sound.name' });

  const { voiceOptions, selectVoice } = useTTSVoiceOptions('minimax', { basePath, strategy });

  // english_normalization only applies to English; clear any stale `true` when
  // leaving 'en' so it doesn't ship in the saved config. Guard on the current
  // value so we don't dirty the form with a no-op write.
  useEffect(() => {
    if (selectedLanguage !== 'en' && englishNormalization) {
      setValue(`${basePath}.english_normalization`, false, { shouldDirty: true });
    }
  }, [selectedLanguage, englishNormalization, setValue, basePath]);

  return (
    <div className="space-y-6">
      {/* Voice Selector */}
      {voiceOptions.length > 0 ? (
        <FormField
          control={control}
          name={`${basePath}.voice_id`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('integrations.voice_common.voice.voice_id')}</FormLabel>
              <FormControl>
                <PopoverCombobox
                  options={voiceOptions}
                  value={field.value || ''}
                  onChange={selectVoice}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : (
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertTriangleIcon size={16} />
          {t('integrations.voice_common.voice.no_voices_found')}
        </div>
      )}

      {/* Speed Slider */}
      <FormField
        control={control}
        name={`${basePath}.speed`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('integrations.voice_common.voice.speed')}</FormLabel>
            <FormControl>
              <Slider
                tooltip
                displayRealValue
                min={0.5}
                max={2}
                step={0.1}
                minLabel={t('integrations.voice_common.voice.slower')}
                maxLabel={t('integrations.voice_common.voice.faster')}
                value={[field.value ?? 1.0]}
                onValueChange={(values) =>
                  setValue(`${basePath}.speed`, values[0], { shouldDirty: true })
                }
              />
            </FormControl>
            <FormDescription>
              {t('integrations.voice_common.voice.speed_description')}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Volume Slider */}
      <FormField
        control={control}
        name={`${basePath}.volume`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('integrations.voice_common.voice.volume')}</FormLabel>
            <FormControl>
              <Slider
                tooltip
                displayRealValue
                // Orchestrator enforces volume > 0 (Joi .greater(0)); 0 is rejected.
                min={0.1}
                max={10}
                step={0.1}
                minLabel={t('integrations.voice_common.voice.lower')}
                maxLabel={t('integrations.voice_common.voice.higher')}
                value={[field.value ?? 1.0]}
                onValueChange={(values) =>
                  setValue(`${basePath}.volume`, values[0], { shouldDirty: true })
                }
              />
            </FormControl>
            <FormDescription>
              {t('integrations.voice_common.voice.volume_description')}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Pitch Slider */}
      <FormField
        control={control}
        name={`${basePath}.pitch`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('integrations.voice_common.voice.pitch')}</FormLabel>
            <FormControl>
              <Slider
                tooltip
                displayRealValue
                min={-12}
                max={12}
                step={1}
                minLabel={t('integrations.voice_common.voice.lower')}
                maxLabel={t('integrations.voice_common.voice.higher')}
                value={[field.value ?? 0]}
                onValueChange={(values) =>
                  setValue(`${basePath}.pitch`, values[0], { shouldDirty: true })
                }
              />
            </FormControl>
            <FormDescription>
              {t('integrations.voice_common.voice.pitch_description')}
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* English Normalization */}
      {selectedLanguage === 'en' && (
        <Switch
          label={t('integrations.voice_common.voice.english_normalization')}
          description={t('integrations.voice_common.voice.english_normalization_description')}
          variant="description"
          switchPosition="right"
          checked={englishNormalization ?? false}
          onCheckedChange={(checked) =>
            setValue(`${basePath}.english_normalization`, checked, { shouldDirty: true })
          }
        />
      )}

      {!strategy && (
        <WaitingSoundsSelector
          value={thinkingSoundName as WaitingSoundName | ''}
          onChange={(value) => setValue('config.thinking_sound.name', value, { shouldDirty: true })}
        />
      )}
    </div>
  );
};

export default MiniMaxVoice;
